import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { crossPlatformStorage } from './storage';

/**
 * Authentication Store
 *
 * Platform differences:
 * - Web: Only stores authentication status (tokens are in HTTP-only cookies)
 * - Mobile: Stores backend URL and manages token storage via expo-secure-store
 *
 * This store provides a unified interface; platform-specific details are handled
 * by the platform's auth initialization code.
 */

interface AuthState {
  /** Whether the user is authenticated */
  isAuthenticated: boolean;

  /** User email (persisted for convenience on login screen) */
  email: string | null;

  /** Backend URL - only used on mobile, empty on web */
  backendUrl: string;

  /** Whether the store has been hydrated from storage */
  _hasHydrated: boolean;

  /** Set authentication status */
  setAuthenticated: (authenticated: boolean) => void;

  /** Set email for persistence */
  setEmail: (email: string | null) => void;

  /** Set backend URL (mobile only) */
  setBackendUrl: (url: string) => void;

  /** Clear backend URL to trigger reconfiguration */
  clearBackendUrl: () => void;

  /** Clear all auth state (logout) */
  logout: () => void;

  /** Mark store as hydrated (internal use) */
  _setHasHydrated: (hydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      isAuthenticated: false,
      email: null,
      backendUrl: '',
      _hasHydrated: false,

      setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),

      setEmail: (email) => set({ email }),

      setBackendUrl: (url) => set({ backendUrl: url }),

      clearBackendUrl: () => set({ backendUrl: '' }),

      logout: () =>
        set({
          isAuthenticated: false,
          // Keep email for convenience, clear other state
        }),

      _setHasHydrated: (hydrated) => set({ _hasHydrated: hydrated }),
    }),
    {
      name: 'smarthome-auth',
      storage: crossPlatformStorage,
      // Only persist non-sensitive data
      partialize: (state) => ({
        email: state.email,
        backendUrl: state.backendUrl,
      }),
      onRehydrateStorage: () => (state) => {
        state?._setHasHydrated(true);
      },
    }
  )
);
